import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Droplets, Award } from "lucide-react";

// Product images from user uploads
const POG_RTD = "https://customer-assets.emergentagent.com/job_ty-core/artifacts/t6qz6lo2_POG%20RTD%204.6.26%202.jpg";
const MADURO = "https://customer-assets.emergentagent.com/job_ty-core/artifacts/mww36b3g_Maduro%2010.16.jpg";
// Ojo de Dios Mezcal
const OJO_MEZCAL = "https://customer-assets.emergentagent.com/job_ty-core/artifacts/b8m9p7dv_Ojo.png";

const Home = () => {
  // SEO - Dynamic page title
  useEffect(() => {
    document.title = "8eight8 Beverage Co | Best Flavored Vodka & Premium Tequila | POG Vodka & Maduro";
  }, []);

  return (
    <div className="bg-[#050505]" data-testid="home-page">
      {/* Hero Section - SEO Optimized */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section" aria-label="Premium Flavored Vodka and Artisan Tequila">
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#FF6B35]/10 to-transparent" />
          <div className="absolute bottom-0 left-0 w-1/2 h-full bg-gradient-to-r from-[#C8A876]/5 to-transparent" />
        </div>

        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <header className="text-left">
              <p className="font-subheading text-xs tracking-[0.3em] uppercase text-[#C8A876] mb-6 animate-fadeInUp">
                Premium Flavored Vodka & Artisan Tequila
              </p>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none text-white mb-8 animate-fadeInUp stagger-1">
                Best Tropical<br />
                <span className="gradient-text-pog">Flavored Vodka</span>
              </h1>
              <p className="text-[#A3A3A3] text-lg md:text-xl max-w-lg mb-10 animate-fadeInUp stagger-2">
                Discover POG Vodka - the #1 passion fruit orange guava flavored vodka, 
                Maduro dark tequila aged in charred oak, and Ojo de Dios artisanal mezcal.
              </p>
              <div className="flex flex-wrap gap-4 animate-fadeInUp stagger-3">
                <Link to="/products" className="btn-pog" data-testid="hero-explore-btn" aria-label="Shop Premium Flavored Vodka and Tequila">
                  Explore Products
                  <ArrowRight className="inline ml-2" size={18} />
                </Link>
              </div>
            </header>

            {/* Right - Empty for cleaner look */}
            <div className="hidden lg:block"></div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 sm:py-32" data-testid="featured-products-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="font-subheading text-xs tracking-[0.3em] uppercase text-[#A3A3A3] mb-4">
              The Collection
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter text-white">
              Three Worlds. One Vision.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* POG Vodka Card */}
            <div 
              className="group relative overflow-hidden card-dark"
              data-testid="pog-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/20 to-[#8A2BE2]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-6 md:p-8">
                <div className="flex flex-col items-center gap-6">
                  <img 
                    src={POG_RTD} 
                    alt="POG Vodka RTD" 
                    className="w-32 h-auto object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="text-center">
                    <p className="font-subheading text-xs tracking-[0.3em] uppercase text-[#FF6B35] mb-2">
                      Tropical Vibes
                    </p>
                    <h3 className="font-heading text-2xl sm:text-3xl tracking-tight text-white mb-3">
                      POG Vodka
                    </h3>
                    <p className="text-[#A3A3A3] text-sm mb-4">
                      Passion Orange Guava. Hawaii's favorite juice, now in vodka form.
                    </p>
                    <Link 
                      to="/products" 
                      className="inline-block text-[#FF6B35] hover:text-[#FF8559] transition-colors font-medium text-sm"
                    >
                      View Products <ArrowRight className="inline ml-1" size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Maduro Tequila Card */}
            <div 
              className="group relative overflow-hidden card-dark"
              data-testid="maduro-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#C8A876]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-6 md:p-8">
                <div className="flex flex-col items-center gap-6">
                  <img 
                    src={MADURO} 
                    alt="Maduro Tequila" 
                    className="w-32 h-auto object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="text-center">
                    <p className="font-subheading text-xs tracking-[0.3em] uppercase text-[#C8A876] mb-2">
                      Dark Luxury
                    </p>
                    <h3 className="font-heading text-2xl sm:text-3xl tracking-tight text-white mb-3">
                      Maduro Tequila
                    </h3>
                    <p className="text-[#A3A3A3] text-sm mb-4">
                      Reposado Oscuro. Black tequila aged in charred oak barrels.
                    </p>
                    <Link 
                      to="/products" 
                      className="inline-block text-[#C8A876] hover:text-[#DDBD90] transition-colors font-medium text-sm"
                    >
                      View Product <ArrowRight className="inline ml-1" size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Ojo de Dios Mezcal Card */}
            <div 
              className="group relative overflow-hidden card-dark"
              data-testid="ojo-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#00CED1]/10 to-[#FFD700]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-6 md:p-8">
                <div className="flex flex-col items-center gap-6">
                  <img 
                    src={OJO_MEZCAL} 
                    alt="Ojo de Dios Mezcal" 
                    className="w-32 h-auto object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="text-center">
                    <p className="font-subheading text-xs tracking-[0.3em] uppercase text-[#00CED1] mb-2">
                      Modern Mexico
                    </p>
                    <h3 className="font-heading text-2xl sm:text-3xl tracking-tight text-white mb-3">
                      Ojo de Dios
                    </h3>
                    <p className="text-[#A3A3A3] text-sm mb-4">
                      Artisanal Mezcal. Bold, innovative. Joven, Cafe & Hibiscus.
                    </p>
                    <Link 
                      to="/products" 
                      className="inline-block text-[#00CED1] hover:text-[#40E0D0] transition-colors font-medium text-sm"
                    >
                      View Collection <ArrowRight className="inline ml-1" size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32" data-testid="cta-section">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter text-white mb-6">
              Ready to Experience?
            </h2>
            <p className="text-[#A3A3A3] text-lg mb-10">
              Find 8eight8 Beverage Co products at select retailers near you. 
              Available at Kroger, Safeway, Albertsons, Foodland, and more.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/where-to-buy" className="btn-pog" data-testid="find-retailers-btn">
                Find Retailers
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="contact-btn">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
